import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import GrillaEffect from 'views/Grillas/GrillaEffect';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { useHistory } from 'react-router-dom';

const RegistrosSinCoincidencias = () => {
  const defaultFecha = [
    { id: 'fecha_valor', value: [dayjs().subtract(60, 'days'), dayjs(new Date())] }
  ];
  const [registros, setRegistros] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [tipoRegistro, setTipoRegistro] = useState('todos');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const history = useHistory();

  const showToast = (title, description = '', severity = 'info') => {
    setSnackbar({ open: true, message: description ? `${title}: ${description}` : title, severity });
  };

  const handleClearFilters = () => {
    setFilters([]);
    showToast('Filtros limpiados', 'Se han eliminado todos los filtros aplicados.', 'info');
  };

  const handleTipoRegistroChange = (nuevoTipo) => {
    setTipoRegistro(nuevoTipo);
    const labels = { todos: 'Todos los registros', lops: 'Solo LOPS', extbc: 'Solo EXTBC' };
    showToast('Filtro de tipo actualizado', `Mostrando: ${labels[nuevoTipo]}`, 'info');
  };

  const NoResultsMessage = () => (
    <Stack spacing={2} sx={{ p: 4, textAlign: 'center', alignItems: 'center' }}>
      <SearchOffIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
      <Typography variant="h6" color="text.secondary">
        No se encontraron registros sin coincidencias con los filtros aplicados
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Intenta ajustar los criterios de búsqueda o limpiar los filtros
      </Typography>
      <Button startIcon={<ClearAllIcon />} variant="outlined" onClick={handleClearFilters}>
        Limpiar todos los filtros
      </Button>
    </Stack>
  );

  const customTopToolbar = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <IconButton
        color="secondary"
        disabled={filters.length === 0}
        onClick={handleClearFilters}
        title="Limpiar Filtros"
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, borderRadius: 1 }}
      >
        <ClearAllIcon /> Limpiar Filtros
      </IconButton>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
        <FilterAltIcon color="action" fontSize="small" />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Mostrar</InputLabel>
          <Select
            value={tipoRegistro}
            label="Mostrar"
            onChange={(e) => handleTipoRegistroChange(e.target.value)}
          >
            <MenuItem value="todos">Todos los registros</MenuItem>
            <MenuItem value="lops">Solo LOPS</MenuItem>
            <MenuItem value="extbc">Solo EXTBC</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Button
        variant="contained"
        color="secondary"
        size="small"
        startIcon={<ArrowBackIcon />}
        onClick={() => history.push('/admin/conciliarops')}
      >
        Volver a Conciliar OPS
      </Button>
      <Typography variant="body2" color="text.secondary">
        {filters.length > 0 && ` | ${filters.length} filtros aplicados`}
        {` | Tipo: ${tipoRegistro === 'todos' ? 'Todos' : tipoRegistro.toUpperCase()}`}
      </Typography>
    </Box>
  );

  const onRowSelectionChange = (rowSelection, table) => {
    const selected = Object.keys(rowSelection)
      .filter(rowId => rowSelection[rowId])
      .map(rowId => table.getRowModel().rows.find(r => r.id === rowId)?.original)
      .filter(Boolean);
    setSelectedRows(selected);
  };

  const columnNames = {
    tipo: 'Tipo',
    fecha_movimiento: 'Fecha Movimiento',
    fecha_valor: 'Fecha Valor',
    num_comprobante: 'N° Comprobante/Referencia',
    descripcion: 'Descripción',
    razonSocial: 'Razón Social',
    debito: 'Débito',
    credito: 'Crédito',
    descripcionConcepto: 'Concepto/Cuenta',
    id_registro: 'ID Registro',
  };

  const colsGrillaHidden = {
    id_registro: false,
    id_comprobante_item: false,
    id_comprobante: false,
  };

  return (
    <Box sx={{ p: 2, pt: '120px' }}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
        <Stack spacing={3}>
          <Box>
            <Typography variant="h5" fontWeight="bold" color="text.primary" gutterBottom>
              Registros Sin Coincidencias
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Registros que no aparecen en ninguna de las tablas de coincidencias de ConciliarOps
            </Typography>
          </Box>

          {registros.length === 0 && !isLoading && filters.length > 0 ? (
            <NoResultsMessage />
          ) : (
            <GrillaEffect
              data={registros}
              updateData={setRegistros}
              filters={filters}
              updateFilters={setFilters}
              colsHeaders={columnNames}
              hiddenColumns={colsGrillaHidden}
              method="GET"
              baseURI={`${process.env.REACT_APP_API_URL}/tesoreriacomprobantes/items/registros-sin-coincidencias`}
              customTopToolbar={customTopToolbar}
              onRowSelectionChange={onRowSelectionChange}
              isLoading={isLoading}
              additionalParams={{ tipo_registro: tipoRegistro }}
              rowSelectionEnabled={true}
            />
          )}
        </Stack>
      </LocalizationProvider>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RegistrosSinCoincidencias;
